import type { DateFilter } from '@/types/scheduling-api';

/**
 * Calendar actor tabs (PROD-2007, ruling 2026-09-12): the calendar page shows
 * EITHER practitioners OR devices — never both in one grid, no "both" option.
 *
 * The active tab rides in the URL as `?actor=devices` next to `?date=&view=`
 * so a device day is shareable and the view-appointment page's Back lands on
 * the right tab. Practitioners is the default and is written as the ABSENCE
 * of the param, so every existing calendar URL keeps its exact shape.
 */
export type CalendarActor = 'practitioners' | 'devices';

export const CALENDAR_ACTOR_PARAM = 'actor';
export const DEFAULT_CALENDAR_ACTOR: CalendarActor = 'practitioners';

/** Anything that is not literally `devices` is the default tab. */
export function parseCalendarActor(raw: string | null | undefined): CalendarActor {
  return raw === 'devices' ? 'devices' : DEFAULT_CALENDAR_ACTOR;
}

/**
 * Mirror the actor into a URLSearchParams in place. Devices sets the param;
 * Practitioners removes it (default = no param, URL unchanged for today's
 * calendar).
 */
export function writeCalendarActor(params: URLSearchParams, actor: CalendarActor): void {
  if (actor === 'devices') params.set(CALENDAR_ACTOR_PARAM, 'devices');
  else params.delete(CALENDAR_ACTOR_PARAM);
}

/**
 * The Devices tab is "the same day/week grid" — Month / Room / Doctor are
 * practitioner views with no device meaning.
 */
export const DEVICE_CALENDAR_VIEWS: readonly DateFilter[] = ['Day', 'Week'];

/** A view the Devices tab can show; anything else falls back to Day. */
export function clampDeviceView(view: DateFilter): DateFilter {
  return DEVICE_CALENDAR_VIEWS.includes(view) ? view : 'Day';
}

/**
 * PROD-2845: the actor the calendar actually shows. With devices disabled
 * (the `VITE_RESOURCES_TAB` release switch AND the organization's "books
 * devices" switch) there is no Devices view, so any request for it — a tab
 * click left in state, a `?actor=devices` link — reads as Practitioners.
 */
export function resolveCalendarActor(requested: CalendarActor, devicesEnabled: boolean): CalendarActor {
  return devicesEnabled ? requested : DEFAULT_CALENDAR_ACTOR;
}
